import React from 'react';
import {
    Create,
    Edit,
    FileField,
    FileInput,
    ImageField,
    ImageInput,
    required,
    SimpleForm,
    useRecordContext,
    usePermissions,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {Avatar, Box, Typography} from '@mui/material';
import {DeleteOutline, PersonAddAltOutlined} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useCharityChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';
import DynamicMetadataFields from '../components/DynamicMetadataFields';

const GENDER_CHOICES = (translate) => [
    {id: 'MALE',   name: translate('Male')},
    {id: 'FEMALE', name: translate('Female')},
    {id: 'OTHER',  name: translate('Other')},
];

const CONTACT_FREQ_CHOICES = (translate) => [
    {id: 'DAILY',         name: translate('DAILY')},
    {id: 'WEEKLY',        name: translate('WEEKLY')},
    {id: 'MONTHLY',       name: translate('MONTHLY')},
    {id: 'QUARTERLY',     name: translate('QUARTERLY')},
    {id: 'SEMI_ANNUALLY', name: translate('SEMI_ANNUALLY')},
    {id: 'ANNUALLY',      name: translate('ANNUALLY')},
];

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton
            variant="primary"
            disabled={saving}
            onClick={handleSubmit(save)}
        >
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const HeaderBar = ({mode, record, translate, onAddSponsor, onDelete, canAddSponsor, canDelete}) => {
    const fullName = record
        ? ([record.first_name, record.last_name].filter(Boolean).join(' ') || 'Unnamed')
        : null;

    return (
        <PageHeader
            breadcrumb={{label: translate('Children'), to: '/children'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create child')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record?.verified ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    {/*
                      * D6 — one entry point. This replaced two buttons, "Select existing sponsor"
                      * and "Add New Sponsor", which gated on the identical expression and led to
                      * two screens that never met. Both cases are now answered on the page this
                      * opens: search for a sponsor, or fill in a new one.
                      */}
                    {mode === 'edit' && canAddSponsor && (
                        <PrimaryButton
                            variant="secondary"
                            startIcon={<PersonAddAltOutlined sx={{fontSize: 16}}/>}
                            onClick={onAddSponsor}
                        >
                            {translate('Add New Sponsor')}
                        </PrimaryButton>
                    )}
                    <SaveAction label={mode === 'create' ? translate('Create child') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const PhotoPanel = ({translate, mode}) => {
    const record = useRecordContext();
    const existingPhoto = record?.profile_image && record?.image_ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_image}.${record.image_ext}`
        : null;

    return (
        <SectionCard title={translate('Profile photo')}>
            {existingPhoto && (
                <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                    <Avatar
                        src={existingPhoto}
                        sx={{
                            width: 120, height: 120,
                            bgcolor: t.color.sidebarHover,
                            color: t.color.text.secondary,
                            border: `1px solid ${t.color.border.hairline}`,
                        }}
                    />
                </Box>
            )}
            <ImageInput
                source="profile_image"
                label={false}
                accept={{'image/*': []}}
                maxSize={3000000}
                validate={mode === 'create' ? required() : undefined}
                sx={{
                    '& .RaFileInput-dropZone': {
                        border: `1px dashed ${t.color.border.default}`,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: t.color.surface,
                        padding: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: t.color.text.secondary,
                        fontSize: `${t.type.caption.size}px`,
                        '&:hover': {
                            borderColor: t.color.accent.primary,
                            color: t.color.accent.primary,
                        },
                    },
                }}
            >
                <ImageField source="src" title="Profile image"/>
            </ImageInput>
            <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: 1}}>
                PNG or JPG. Maximum 3 MB.
            </Typography>
        </SectionCard>
    );
};

const VideoPanel = ({translate}) => (
    <SectionCard title={translate('Introduction video')}>
        <FileInput
            source="profile_video"
            label={false}
            accept={{'video/*': []}}
            sx={{
                '& .RaFileInput-dropZone': {
                    border: `1px dashed ${t.color.border.default}`,
                    borderRadius: `${t.radius.sm}px`,
                    backgroundColor: t.color.surface,
                    padding: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    color: t.color.text.secondary,
                    fontSize: `${t.type.caption.size}px`,
                    '&:hover': {
                        borderColor: t.color.accent.primary,
                        color: t.color.accent.primary,
                    },
                },
            }}
        >
            <FileField source="src" title="Introduction video"/>
        </FileInput>
    </SectionCard>
);

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const navigate = useNavigate();
    const {permissions} = usePermissions();
    const charityChoices = useCharityChoices();
    const deleteConfirm = useDeleteConfirm();

    const charityUuidChoices = charityChoices
        .filter((c) => c.external_id || String(c.id).includes('-'))
        .map((c) => ({id: c.external_id ?? c.id, name: c.name}));
    const defaultCharityId = charityUuidChoices.length === 1 ? charityUuidChoices[0].id : undefined;

    if (!charityChoices.length) return null;
    if (mode === 'edit' && !record) return null;

    const fullName = record
        ? [record.first_name, record.last_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canAddSponsor={!!record && !record.child_is_sponsored && hasPermission(permissions, ['match'], 'guardians')}
                canDelete={!!record && !record.child_is_sponsored && hasPermission(permissions, ['delete'], 'children.delete')}
                onAddSponsor={() => navigate('/guardians/create', {state: {child: record}})}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="children"
                    record={record}
                    recordLabel={fullName}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Identity')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="first_name" label={translate('First name')} required/>
                            <StatamicTextInput source="last_name"  label={translate('Last name')}  required/>
                            <StatamicTextInput source="birth_date" label={translate('Birth date')} type="date" required/>
                            <StatamicSelectInput
                                source="gender"
                                label={translate('Gender')}
                                required
                                choices={GENDER_CHOICES(translate)}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Child story')}>
                        <StatamicTextInput
                            source="intro"
                            label={translate('What do I dream of? What makes me happy?')}
                            required
                            multiline
                            rows={3}
                        />
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput
                                source="family"
                                label={translate('My story')}
                                required
                                multiline
                                rows={4}
                            />
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput
                                source="help"
                                label={translate('Why do I need help')}
                                required
                                multiline
                                rows={3}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Additional Information')}>
                        <DynamicMetadataFields entityType="CHILD" mode="edit"/>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        {mode === 'edit' && hasPermission(permissions, ['create', 'edit'], 'children.verified') && (
                            <ToggleRow
                                source="verified"
                                label={translate('Verified')}
                                subtitle="Profile reviewed and approved"
                                defaultValue={false}
                            />
                        )}
                        {hasPermission(permissions, ['show'], 'umbrella') && (
                            <ToggleRow
                                source="project_level"
                                label={translate('Project level')}
                                subtitle="Child is sponsored at project level"
                                defaultValue={false}
                            />
                        )}
                    </SectionCard>

                    <SectionCard title={translate('Classification')}>
                        {hasPermission(permissions, ['create', 'edit'], 'charityChoices') && (
                            <StatamicSelectInput
                                source="charity_external_id"
                                label={translate('Charity')}
                                required
                                choices={charityUuidChoices}
                                defaultValue={defaultCharityId}
                            />
                        )}
                        <Box sx={{mt: 1.5}}>
                            <StatamicSelectInput
                                source="contact_frequency"
                                label={translate('Contact frequency')}
                                choices={CONTACT_FREQ_CHOICES(translate)}
                                defaultValue="MONTHLY"
                            />
                        </Box>
                    </SectionCard>

                    <PhotoPanel translate={translate} mode={mode}/>
                    <VideoPanel translate={translate}/>
                </Box>
            </Box>
        </Box>
    );
};

export const ChildEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const ChildCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default ChildEditStatamic;
